import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import axios from "../../utils/axios";
import { IDeleteApiResponse, IError, apiLimit } from "../../utils/api.types";
import { VocuherCategory } from "../../utils/common-utils";
import {
  AReports,
  ReportFetch,
  ALedgerReports,
  LowStockResponse,
  RateListResponse,
  InventoryReportFetch,
  ItemReportByPartyResponse,
  ItemReportByPartyFetch,
  ItemPurchaseSummaryResponse,
  ItemPurchaseSummaryFetch,
  PartyWiseOutstandingResponse,
  PartyWiseOutstandingFetch,
  ItemSalesAndPurchaseSummaryResponse,
  ItemSalesAndPurchaseSummaryFetch
} from "../../utils/report.types";

/**
 * Fetch Invoices async thunk.
*/
export const fetchReport = createAsyncThunk(
    'report/fetchReport',
    async ({accountId, type, isShareExcel, params} : ReportFetch): Promise<AReports> => {
        let queryParam = (params)? `&${params}`: ''
        if(type) {
            queryParam += `&voucherCategory=${type}`
        }
        if(isShareExcel) {
            queryParam += `&isShareExcel=${isShareExcel}`
        }
        const response = await axios.get(`/api/v1/reports/sale-report?limit=50${queryParam}`);
        return response.data;
    }
);

export const downloadGstrReport = createAsyncThunk(
    'report/downloadGstrReport',
    async ({accountId, type, isDownloadPdf, params} : ReportFetch): Promise<any> => {
        let queryParam = (params)? `&${params}`: ''
        if(type) {
            queryParam += `&voucherCategory=${type}`
        }
        if(isDownloadPdf) {
            queryParam += `&isDownloadPdf=${isDownloadPdf}`
        }
        const response = await axios.get(`/api/v1/reports/sale-report?limit=50${queryParam}`, {
            responseType: "blob", // Ensure response is a Blob object
        });
        return response.data;
    }
);

export const downloadGstrExcelReport = createAsyncThunk(
    'report/downloadGstrExcelReport',
    async ({accountId, type, isDownloadExcel, params} : ReportFetch): Promise<any> => {
        let queryParam = (params)? `&${params}`: ''
        if(type) {
            queryParam += `&voucherCategory=${type}`
        }
        if(isDownloadExcel) {
            queryParam += `&isDownloadExcel=${isDownloadExcel}`
        }
        const response = await axios.get(`/api/v1/reports/sale-report?limit=50${queryParam}`, {
            responseType: "blob", // Ensure response is a Blob object
        });
        return response.data;
    }
);

export const fetchPartyLedgerReport = createAsyncThunk(
    'report/fetchPartyLedgerReport',
    async ({accountId, type, isShareExcel, params} : ReportFetch): Promise<ALedgerReports> => {
        let queryParam = (params)? `${params}`: ''
        if(isShareExcel) {
            queryParam += `&isShareExcel=${isShareExcel}`
        }
        const response = await axios.get(`/api/v1/reports/party-ledger?${queryParam}`);
        return response.data;
    }
);

export const downloadPartyLedgerReport = createAsyncThunk(
    'report/downloadPartyLedgerReport',
    async ({accountId, type, isDownloadExcel, isDownloadPdf, params} : ReportFetch): Promise<any> => {
        let queryParam = (params)? `${params}`: ''
        if(isDownloadPdf) {
            queryParam += `&isDownloadPdf=${isDownloadPdf}`
        }
        const response = await axios.get(`/api/v1/reports/party-ledger?${queryParam}`, {
            responseType: "blob", // Ensure response is a Blob object
        });
        return response.data;
    }
);

// TDS/TCS Report Thunks
export const fetchTdsPayableReport = createAsyncThunk(
    'report/fetchTdsPayableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-payable${queryParam}`);
        return response.data;
    }
);

export const downloadTdsPayableReport = createAsyncThunk(
    'report/downloadTdsPayableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-payable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const downloadTdsPayableExcelReport = createAsyncThunk(
    'report/downloadTdsPayableExcelReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = params ? `?${params}&isDownloadExcel=true` : '?isDownloadExcel=true'
        const response = await axios.get(`/api/v1/reports/tds-payable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const fetchTdsReceivableReport = createAsyncThunk(
    'report/fetchTdsReceivableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-receivable${queryParam}`);
        return response.data;
    }
);

export const downloadTdsReceivableReport = createAsyncThunk(
    'report/downloadTdsReceivableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tds-receivable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const downloadTdsReceivableExcelReport = createAsyncThunk(
    'report/downloadTdsReceivableExcelReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = params ? `?${params}&isDownloadExcel=true` : '?isDownloadExcel=true'
        const response = await axios.get(`/api/v1/reports/tds-receivable${queryParam}`, {
            responseType: "blob",
        });
        return response.data;
    }
);

export const fetchTcsPayableReport = createAsyncThunk(
    'report/fetchTcsPayableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tcs-payable${queryParam}`);
        return response.data;
    }
);

export const fetchTcsReceivableReport = createAsyncThunk(
    'report/fetchTcsReceivableReport',
    async ({accountId, params} : {accountId: string, params?: string}): Promise<any> => {
        let queryParam = (params)? `?${params}`: ''
        const response = await axios.get(`/api/v1/reports/tcs-receivable${queryParam}`);
        return response.data;
    }
);

// Low Stock Summary Report Thunks
export const fetchLowStockSummaryReport = createAsyncThunk(
    'report/fetchLowStockSummaryReport',
    async (params: InventoryReportFetch): Promise<LowStockResponse> => {
        const queryParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });
        
        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/low-stock-summary${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadLowStockSummaryReport = createAsyncThunk(
    'report/downloadLowStockSummaryReport',
    async (params: InventoryReportFetch): Promise<any> => {
        const queryParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });
        
        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/low-stock-summary${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Rate List Report Thunks
export const fetchRateListReport = createAsyncThunk(
    'report/fetchRateListReport',
    async (params: InventoryReportFetch): Promise<RateListResponse> => {
        const queryParams = new URLSearchParams();
        
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });
        
        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/rate-list${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadRateListReport = createAsyncThunk(
    'report/downloadRateListReport',
    async (params: InventoryReportFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/rate-list${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Item Report By Party Thunks
export const fetchItemReportByParty = createAsyncThunk(
    'report/fetchItemReportByParty',
    async (params: ItemReportByPartyFetch): Promise<ItemReportByPartyResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/item-report-by-party${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadItemReportByParty = createAsyncThunk(
    'report/downloadItemReportByParty',
    async (params: ItemReportByPartyFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/item-report-by-party${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Item Purchase Summary Report Thunks
export const fetchItemPurchaseSummary = createAsyncThunk(
    'report/fetchItemPurchaseSummary',
    async (params: ItemPurchaseSummaryFetch): Promise<ItemPurchaseSummaryResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/item-purchase-summary${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadItemPurchaseSummary = createAsyncThunk(
    'report/downloadItemPurchaseSummary',
    async (params: ItemPurchaseSummaryFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/item-purchase-summary${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Party Wise Outstanding Report Thunks
export const fetchPartyWiseOutstanding = createAsyncThunk(
    'report/fetchPartyWiseOutstanding',
    async (params: PartyWiseOutstandingFetch): Promise<PartyWiseOutstandingResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/party-wise-outstanding${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadPartyWiseOutstanding = createAsyncThunk(
    'report/downloadPartyWiseOutstanding',
    async (params: PartyWiseOutstandingFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/party-wise-outstanding${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);

// Item Sales and Purchase Summary Report Thunks
export const fetchItemSalesAndPurchaseSummary = createAsyncThunk(
    'report/fetchItemSalesAndPurchaseSummary',
    async (params: ItemSalesAndPurchaseSummaryFetch): Promise<ItemSalesAndPurchaseSummaryResponse> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(`/api/v1/reports/item-sales-and-purchase-summary${queryString ? `?${queryString}` : ''}`);
        return response.data;
    }
);

export const downloadItemSalesAndPurchaseSummary = createAsyncThunk(
    'report/downloadItemSalesAndPurchaseSummary',
    async (params: ItemSalesAndPurchaseSummaryFetch): Promise<any> => {
        const queryParams = new URLSearchParams();

        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, String(value));
            }
        });

        const queryString = queryParams.toString();
        const response = await axios.get(
            `/api/v1/reports/item-sales-and-purchase-summary${queryString ? `?${queryString}` : ''}`,
            {
                responseType: "blob", // Ensure response is a Blob object
            }
        );
        return response.data;
    }
);